import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { repositorioPanel } from '../../../data/repositories/RepositorioPanel';
import { repositorioSensores } from '../../../data/repositories/RepositorioSensores';
import { PanelPersonal, type ColorMarcador } from '../../../domain/entities/PanelPersonal';
import type { Sensor } from '../../../domain/entities/Sensor';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { useSesionStore } from '../../stores/sesionStore';

/** Espera antes de guardar automáticamente tras un cambio. */
const RETARDO_GUARDADO_MS = 1200;

export type EstadoGuardado = 'guardado' | 'guardando' | 'pendiente' | 'error';

/**
 * Estado del panel personal.
 *
 * Los cambios se guardan solos poco después de hacerlos, sin botón. Obligar a
 * pulsar «guardar» tras arrastrar un marcador es una forma segura de que la
 * gente pierda su trabajo.
 */
export function usePanel() {
  const perfil = useSesionStore((s) => s.perfil);
  const usuarioId = perfil?.id ?? '';

  const [panel, setPanel] = useState<PanelPersonal | null>(null);
  const [editando, setEditando] = useState(false);
  const [colorActivo, setColorActivo] = useState<ColorMarcador>('verde');
  const [guardado, setGuardado] = useState<EstadoGuardado>('guardado');
  const [subiendoImagen, setSubiendoImagen] = useState(false);

  /*
   * Todos los paneles de la persona, no sólo el abierto.
   *
   * Se guardan juntos en una sola escritura: son una colección, y guardar sólo
   * el activo dejaría a los demás sin escribir cada vez que se cambia de uno.
   */
  const [paneles, setPaneles] = useState<PanelPersonal[]>([]);

  const temporizadorGuardado = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<PanelPersonal | null>(null);
  panelRef.current = panel;

  // Lo último conocido de la colección, para poder guardarla desde un
  // temporizador sin arrastrar dependencias que la recrearían.
  const panelesRef = useRef<PanelPersonal[]>([]);
  panelesRef.current = paneles;

  const consultaPanel = useQuery({
    queryKey: ['panel', usuarioId],
    queryFn: () => repositorioPanel.obtenerTodos(usuarioId),
    enabled: usuarioId !== '',
  });

  useEffect(() => {
    if (!consultaPanel.data) return;

    setPaneles(consultaPanel.data.paneles);

    const activo =
      consultaPanel.data.paneles.find((p) => p.id === consultaPanel.data.activoId) ??
      consultaPanel.data.paneles[0] ??
      null;

    setPanel(activo);
  }, [consultaPanel.data]);

  // Los sensores se leen enteros porque el panel puede apuntar a cualquiera y
  // la lista completa se reutiliza en el selector.
  const consultaSensores = useQuery({
    queryKey: ['sensores', {}],
    queryFn: () => repositorioSensores.listarConValores({}),
  });

  const sensores = useMemo(() => consultaSensores.data ?? [], [consultaSensores.data]);

  const sensoresPorId = useMemo(() => {
    const mapa = new Map<number, Sensor>();
    for (const s of sensores) mapa.set(s.id, s);
    return mapa;
  }, [sensores]);

  /** Sustituye el panel dado dentro de la colección, o lo añade si es nuevo. */
  const conElPanel = (lista: readonly PanelPersonal[], uno: PanelPersonal): PanelPersonal[] => {
    const existe = lista.some((p) => p.id === uno.id);
    return existe ? lista.map((p) => (p.id === uno.id ? uno : p)) : [...lista, uno];
  };

  const guardar = useCallback(async (siguiente: PanelPersonal) => {
    setGuardado('guardando');
    try {
      const coleccion = conElPanel(panelesRef.current, siguiente);
      setPaneles(coleccion);
      await repositorioPanel.guardarTodos(siguiente.usuarioId, coleccion, siguiente.id);
      setGuardado('guardado');
    } catch (e) {
      registrarError('panel.guardar', e);
      setGuardado('error');
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudieron guardar los cambios');
    }
  }, []);

  /** Aplica un cambio y programa su guardado. */
  const cambiar = useCallback(
    (transformar: (actual: PanelPersonal) => PanelPersonal) => {
      setPanel((actual) => {
        if (!actual) return actual;

        const siguiente = transformar(actual);
        setGuardado('pendiente');

        if (temporizadorGuardado.current) clearTimeout(temporizadorGuardado.current);
        temporizadorGuardado.current = setTimeout(
          () => void guardar(siguiente),
          RETARDO_GUARDADO_MS,
        );

        return siguiente;
      });
    },
    [guardar],
  );

  // Un guardado pendiente al desmontar se perdería; se ejecuta de inmediato.
  useEffect(() => {
    return () => {
      if (temporizadorGuardado.current) clearTimeout(temporizadorGuardado.current);
    };
  }, []);

  /** Guarda ya, sin esperar al diferido. Devuelve si se pudo. */
  const guardarAhora = useCallback(async (): Promise<boolean> => {
    if (temporizadorGuardado.current) {
      clearTimeout(temporizadorGuardado.current);
      temporizadorGuardado.current = null;
    }

    const actual = panelRef.current;
    if (!actual) return false;

    setGuardado('guardando');
    try {
      const coleccion = conElPanel(panelesRef.current, actual);
      setPaneles(coleccion);
      await repositorioPanel.guardarTodos(actual.usuarioId, coleccion, actual.id);
      setGuardado('guardado');
      return true;
    } catch (e) {
      registrarError('panel.guardar', e);
      setGuardado('error');
      return false;
    }
  }, []);

  /**
   * Abre otro panel.
   *
   * Antes se guarda lo que hubiera pendiente del actual: cambiar de panel no
   * puede tirar los últimos segundos de trabajo por el retardo del guardado.
   */
  const abrirPanel = useCallback(
    async (panelId: string) => {
      if (temporizadorGuardado.current) {
        clearTimeout(temporizadorGuardado.current);
        temporizadorGuardado.current = null;
        const actual = panelRef.current;
        if (actual) await guardar(actual);
      }

      const elegido = panelesRef.current.find((p) => p.id === panelId);
      if (elegido) setPanel(elegido);
    },
    [guardar],
  );

  /** Crea un panel vacío y lo abre. */
  const crearPanel = useCallback(
    async (nombre: string) => {
      const nuevo = PanelPersonal.vacio(usuarioId, nombre.trim() || 'Panel nuevo');
      const coleccion = [...panelesRef.current, nuevo];

      setPaneles(coleccion);
      setPanel(nuevo);

      setGuardado('guardando');
      try {
        await repositorioPanel.guardarTodos(usuarioId, coleccion, nuevo.id);
        setGuardado('guardado');
      } catch (e) {
        registrarError('panel.crear', e);
        setGuardado('error');
        toast.error(e instanceof ErrorApp ? e.message : 'No se pudo crear el panel');
      }
    },
    [usuarioId],
  );

  /** Borra un panel. Nunca se queda sin ninguno. */
  const borrarPanel = useCallback(
    async (panelId: string) => {
      const restantes = panelesRef.current.filter((p) => p.id !== panelId);
      if (restantes.length === 0) {
        toast.error('Tiene que quedar al menos un panel');
        return;
      }

      const siguiente = restantes[0]!;
      setPaneles(restantes);
      setPanel(siguiente);

      try {
        await repositorioPanel.guardarTodos(usuarioId, restantes, siguiente.id);
      } catch (e) {
        registrarError('panel.borrar', e);
        toast.error(e instanceof ErrorApp ? e.message : 'No se pudo borrar el panel');
      }
    },
    [usuarioId],
  );

  /** Pone una cámara de fondo, o la quita pasando `null`. */
  const elegirCamara = useCallback(
    (camaraId: number | null) => {
      cambiar((actual) => actual.conCamara(camaraId));
    },
    [cambiar],
  );

  const subirImagen = useCallback(
    async (archivo: File) => {
      if (!panel) return;

      setSubiendoImagen(true);
      try {
        const url = await repositorioPanel.subirImagen(usuarioId, archivo);
        cambiar((actual) => actual.conImagen(url));
        toast.success('Plano actualizado');
      } catch (e) {
        registrarError('panel.subirImagen', e);
        toast.error(e instanceof ErrorApp ? e.message : 'No se pudo subir la imagen');
      } finally {
        setSubiendoImagen(false);
      }
    },
    [panel, usuarioId, cambiar],
  );

  const refrescarValores = useCallback(() => {
    void consultaSensores.refetch();
  }, [consultaSensores]);

  useTiempoReal({ tabla: 'last_sensor_values', evento: '*' }, refrescarValores);
  useTiempoReal({ tabla: 'sensores_runtime', evento: 'UPDATE' }, refrescarValores);

  /** Datos ya resueltos de un sensor referenciado por el panel. */
  const sensorDe = useCallback(
    (sensorId: number | null) => (sensorId === null ? null : (sensoresPorId.get(sensorId) ?? null)),
    [sensoresPorId],
  );

  return {
    panel,
    sensores,
    sensorDe,
    cargando: consultaPanel.isLoading || consultaSensores.isLoading,
    error: consultaPanel.isError,

    editando,
    alternarEdicion: () => setEditando((v) => !v),
    colorActivo,
    setColorActivo,
    guardado,
    subiendoImagen,

    cambiar,
    guardarAhora,
    subirImagen,
    elegirCamara,

    paneles,
    abrirPanel,
    crearPanel,
    borrarPanel,
  };
}
